import { deserialize, type CborMap } from '@mesh/friendly-cbor-serializer'
import { MeshAgent } from '../agent'
import { portalHost } from '../environment'
import {
  MeshAppMessage,
  MeshAppNavigationMessage,
  MeshAppRequestMessage,
  navigationMessageKind,
  requestMessageKind,
} from './message'
import { ResponseHeaderSchema } from '../agent/messages/responseHeader'
import AgentStatusCodes from '../agent/agentStatusCodes'
import { LookupSiteResponse } from '../agent/messages'
import AgentMessageTypes from '../agent/agentMessageTypes'
import { Id } from '../aliases'

export class MeshAppBridge {
  private appHost?: HTMLIFrameElement
  private meshAgent?: MeshAgent
  private boundHandleMessage: (event: MessageEvent) => void
  private initialData: LookupSiteResponse | null = null

  constructor(meshAgent: MeshAgent) {
    this.meshAgent = meshAgent
    this.boundHandleMessage = this.eventListener.bind(this)
    window.addEventListener('message', this.boundHandleMessage)
    console.log('MeshAppBridge initialized')
  }

  public setAppHost(appHost: HTMLIFrameElement) {
    this.appHost = appHost
  }

  private postInitialData(id: Id) {
    const payloadMap = new Map()
    const initialDataPayload = this.initialData?.payload

    if (initialDataPayload) {
      payloadMap.set(0, initialDataPayload?.proxyLinkCode)
    }
    this.postMessage({
      id: id,
      type: AgentMessageTypes.GetInitialDataResponse,
      status: 0,
      statusMessage: 'Success',
      payload: payloadMap,
    })
  }

  private async eventListener(event: MessageEvent) {
    if (event.origin !== portalHost) return
    if (event.source !== this.appHost?.contentWindow) return

    const request = event.data as MeshAppMessage | undefined | null
    console.log('Received message from app:', request)

    if (!request || !this.meshAgent || !request.kind) return

    if (request.kind === requestMessageKind) {
      await this.handleRequestMessage(request as MeshAppRequestMessage)
    } else if (request.kind === navigationMessageKind) {
      this.handleNavigationMessage(request as MeshAppNavigationMessage)
    }
  }

  private handleNavigationMessage(message: MeshAppNavigationMessage) {
    const url = message.url
    if (url) {
      window.location.href = url
    }
  }

  private async handleRequestMessage(message: MeshAppRequestMessage) {
    const { type, id, payload } = message
    if (!type || !id || !this.meshAgent) return

    try {
      if (type === AgentMessageTypes.GetInitialDataRequest) {
        this.postInitialData(id)
        return
      }

      const cborResponse = await this.meshAgent.buildAndSendMessage({
        payload: payload ?? new Map(),
        messageType: type,
        messageId: id,
      })

      const headerCbor = cborResponse.get(0) as CborMap
      const responseHeader = deserialize(ResponseHeaderSchema, headerCbor)

      const payloadCbor = cborResponse.get(1) as CborMap | undefined

      this.postMessage({
        id: id,
        type: type,
        status: responseHeader.status,
        statusMessage: responseHeader.statusMessage,
        payload: payloadCbor instanceof Map ? payloadCbor : undefined,
      })
    } catch (err) {
      this.postMessage({
        id,
        type,
        status: AgentStatusCodes.CompanionError,
        statusMessage: 'Unknown error',
      })
    }
  }

  private postMessage(message: MeshAppRequestMessage) {
    this.appHost?.contentWindow?.postMessage(message, portalHost)
  }

  public setInitialData(data: LookupSiteResponse) {
    this.initialData = data
  }

  dispose() {
    this.appHost = undefined
    this.meshAgent = undefined
    window.removeEventListener('message', this.boundHandleMessage)
  }
}
